import theme from 'stories/utils/theme'
import styled from 'styled-components'
import NewButton from 'components/elements/NewButton'
import { Body2, Headline3 } from 'stories/elements/Typography/Text'
import Stack from 'components/elements/Stack'
import _useTranslation from 'lib/hooks/_useTranslation'
import QuizzesAsset from './features/QuizzesAsset'
import AiMockExamAsset from './features/AiMockExamAsset'
import { AnalyticsEventHomepageMainFeaturesClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    @media (max-width: 740px) {
        grid-template-columns: 1fr;
    }
`

const Card = styled.div`
    border-radius: ${theme.borderRadius.rounded};
    padding: 24px 24px 48px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    overflow: hidden;
    gap: 20px;
    &:first-child {
        background: #221140;
    }
    &:last-child {
        background: #360914;
    }
    p,
    h3 {
        text-align: center;
    }
    h3 {
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 30px;
        line-height: 36px;
        letter-spacing: -0.4px;
        padding-bottom: 10px;
    }
    .subtitle {
        font-family: var(--font-inter);
        font-weight: 450 !important;
        font-size: 17px;
        line-height: 24px;
        letter-spacing: 0.3px;
    }
    button p {
        font-family: var(--font-inter);
        font-weight: 650 !important;
        font-size: 17px;
        line-height: 20px;
        letter-spacing: 0.3px;
        color: ${theme.colors.black};
    }
    @media (max-width: 750px) {
        padding: 24px;
        h3 {
            font-family: var(--font-inter);
            font-weight: 650;
            font-size: 23px;
            line-height: 28px;
            letter-spacing: -0.2px;
        }
        .subtitle {
            font-family: var(--font-inter);
            font-weight: 450;
            font-size: 17px;
            line-height: 24px;
            letter-spacing: 0.3px;
        }
    }
`

const AssetContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding-bottom: 20px;
`

interface HomepageMainFeaturesProps {
    onButtonClick: (referrerScreen: string) => void
}

const HomepageMainFeatures = ({ onButtonClick }: HomepageMainFeaturesProps) => {
    const { t } = _useTranslation('homepage')

    const cards = [
        {
            title: t('homepage/quizzesTitle'),
            description: t('homepage/quizzesDescription'),
            buttonText: t('homepage/tryItOut'),
            referrerScreen: 'homepage_quizzes',
            asset: <QuizzesAsset />,
        },
        {
            title: t('homepage/mockExamTitle'),
            description: t('homepage/mockExamDescription'),
            buttonText: t('homepage/tryItOut'),
            referrerScreen: 'homepage_mock_exam',
            asset: <AiMockExamAsset />,
        },
    ]

    return (
        <Wrapper>
            {cards.map((card, index) => (
                <Card key={index}>
                    <Stack gutter={20}>
                        <div>
                            <Headline3
                                color={theme.colors.white}
                                fontSize={28}
                                fontFamily="var(--font-inter)"
                                fontWeight={450}
                            >
                                {card.title}
                            </Headline3>
                            <Body2
                                color={theme.colors.grayScaleSecondary}
                                fontWeight={450}
                                fontSize={16}
                                fontFamily="var(--font-inter)"
                                className="description"
                            >
                                {card.description}
                            </Body2>
                        </div>
                    </Stack>
                    <AssetContainer>{card.asset}</AssetContainer>
                    <NewButton
                        onClick={() => {
                            trackEvent(AnalyticsEventHomepageMainFeaturesClick, {
                                referrerScreen: card.referrerScreen,
                                value: card.title,
                            })
                            onButtonClick(card.referrerScreen)
                        }}
                        color={theme.colors.white}
                        borderRadius={theme.borderRadius.webappButton}
                        height={44}
                        width="fit-content"
                    >
                        <p>{card.buttonText}</p>
                    </NewButton>
                </Card>
            ))}
        </Wrapper>
    )
}

export default HomepageMainFeatures
